import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Checkbox } from "../../components/ui/checkbox";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  InvestmentPreference,
  LenderPolicy,
  AssetCategoryRule,
  emptyPreferences,
  policyCompletion,
  normalizeLenderPolicy,
  FREQUENCY_OPTIONS,
  CASE_TYPE_OPTIONS,
  RentalFrequency,
  CaseType,
} from "../../types/lender";
import { LEASING_PRODUCTS } from "../../types/deals";
import { Check, ChevronsUpDown, Percent, X, IndianRupee, Info } from "lucide-react";
import { cn } from "../../lib/utils";
import { Progress } from "../../components/ui/progress";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";

const CREDIT_RATINGS = ["AAA",
    "AA+",
    "AA",
    "AA-",
    "A+",
    "A",
    "A-",
    "BBB+",
    "BBB",
    "BBB-",
    "BB+",
    "BB",
    "BB-",
    "B+",
    "B",
    "B-",
    "CCC+",
    "CCC",
    "CCC-",
    "CC",
    "C",
    "D",
    "Unrated",];

const TABS = [
  { id: "deal", label: "Preferred Deal Terms", hint: "Products, exposure, pricing & case structure" },
  { id: "asset", label: "Asset Terms", hint: "Categories, tenure & preferred assets" },
  { id: "customer", label: "Customer Profile", hint: "Credit, turnover & restrictions" },
] as const;
type TabId = (typeof TABS)[number]["id"];

interface InvestmentPreferenceSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lenderName: string;
  lenderId: string;
  policy?: LenderPolicy;
  onSave: (policy: { id?: string; name: string; preferences: InvestmentPreference }) => void | Promise<void>;
}

function tabComplete(id: TabId, p: InvestmentPreference): boolean {
  if (id === "deal") {
    return (p.preferredProducts || []).length > 0
      && p.minExposure > 0 && p.maxExposure > 0
      && p.discountingFactorMin > 0 && p.discountingFactorMax > 0
      && (p.frequency || []).length > 0
      && (p.caseTypes || []).length > 0;
  }
  if (id === "asset") {
    const rules = p.assetCategoryRules || [];
    return rules.length > 0 && rules.every(r => r.tenureMin > 0 && r.tenureMax > 0);
  }
  return !!p.minCreditRating && p.minTurnover > 0;
}

export function InvestmentPreferenceSheet({ open, onOpenChange, lenderName, lenderId, policy, onSave }: InvestmentPreferenceSheetProps) {
  const [policyName, setPolicyName] = useState("");
  const [prefs, setPrefs] = useState<InvestmentPreference>(emptyPreferences());
  const [activeTab, setActiveTab] = useState<TabId>("deal");
  const [saving, setSaving] = useState(false);
  const [policyActive, setPolicyActive] = useState(true);

  useEffect(() => {
    if (!open) return;

    if (policy) {
      const normalized = normalizeLenderPolicy(policy);
      setPolicyName(normalized.name);
      setPrefs(normalized.preferences);
      setPolicyActive(normalized.active);
    } else {
      setPolicyName("");
      setPrefs(emptyPreferences());
      setPolicyActive(true);
    }
    setActiveTab("deal");
  }, [open, policy]);

  const completion = useMemo(() => policyCompletion(prefs), [prefs]);
  const allTabsValid = TABS.every(t => tabComplete(t.id, prefs));
  const canSave = policyName.trim().length > 0 && allTabsValid;

  const handleSave = async () => {
    if (!canSave || saving) return;

    const payload = {
      id: policy?.id,
      name: policyName.trim(),
      preferences: prefs,
    };

    setSaving(true);
    try {
      if (policy?.id) {
        await apiService.post("/lender/update-lender-policy", {
          lenderId,
          ...payload,
          active: policyActive,
        });
        toast.success("Policy updated successfully!");
      } else {
        await apiService.post("/lender/create-lender-policy", {
          lenderId,
          ...payload,
          active: true,
        });
        toast.success("Policy created successfully!");
      }

      await onSave(payload);
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.message || "Failed to save policy. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const currentIdx = TABS.findIndex(t => t.id === activeTab);
  const isLast = currentIdx === TABS.length - 1;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[880px] p-0 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-br from-[hsl(var(--ai-surface))]/40 to-background">
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="text-base font-semibold leading-tight">Manage Investment Preference</SheetTitle>
              <p className="text-xs text-muted-foreground mt-0.5">{lenderName}</p>
            </div>
            <Badge variant="outline" className="text-[10px]">{policy ? "Editing" : "New Policy"}</Badge>
          </div>
        </div>

        {/* Policy name + completion */}
        <div className="px-6 pt-4 pb-3 border-b grid grid-cols-[1fr_240px] gap-4 items-end">
          <div>
            <Label className="text-xs">Policy Name <span className="text-destructive">*</span></Label>
            <Input
              value={policyName}
              onChange={e => setPolicyName(e.target.value)}
              placeholder="e.g. Enterprise Leasing FY26"
              className="h-9 mt-1"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Overall Completion</Label>
              <span className={cn("text-xs font-semibold", completion === 100 ? "text-[hsl(var(--status-success))]" : "text-muted-foreground")}>
                {completion}%
              </span>
            </div>
            <Progress value={completion} className="h-2" />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 grid grid-cols-[260px_1fr] min-h-0">
          {/* Left rail */}
          <div className="border-r bg-muted/20 p-3 space-y-1.5">
            {TABS.map((t, i) => {
              const done = tabComplete(t.id, prefs);
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={cn(
                    "w-full text-left rounded-lg px-3 py-3 text-sm transition-all border",
                    active
                      ? "bg-primary/10 border-primary/30 text-foreground shadow-sm"
                      : "bg-card hover:bg-muted border-transparent"
                  )}
                >
                  <div className="flex items-start gap-2.5">
                    <span className={cn(
                      "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5",
                      done ? "bg-[hsl(var(--status-success))] text-white" :
                      active ? "bg-primary text-primary-foreground" : "bg-muted-foreground/20 text-muted-foreground"
                    )}>
                      {done ? <Check className="h-3 w-3" /> : i + 1}
                    </span>
                    <div className="min-w-0">
                      <div className={cn("font-medium text-[13px]", active && "text-primary")}>{t.label}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{t.hint}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right content */}
          <ScrollArea className="h-full">
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">{currentIdx + 1}. {TABS[currentIdx].label}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{TABS[currentIdx].hint}</p>
                </div>
                <Badge variant={tabComplete(activeTab, prefs) ? "default" : "outline"} className="text-[10px]">
                  {tabComplete(activeTab, prefs) ? "Complete" : "Incomplete"}
                </Badge>
              </div>

              {activeTab === "deal" && <DealTermsTab prefs={prefs} setPrefs={setPrefs} />}
              {activeTab === "asset" && <AssetTermsTab prefs={prefs} setPrefs={setPrefs} />}
              {activeTab === "customer" && <CustomerProfileTab prefs={prefs} setPrefs={setPrefs} />}
            </div>
          </ScrollArea>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t flex items-center justify-between bg-background">
          <Button
            variant="outline"
            disabled={currentIdx === 0}
            onClick={() => setActiveTab(TABS[Math.max(0, currentIdx - 1)].id)}
          >
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            {!isLast ? (
              <Button onClick={() => setActiveTab(TABS[currentIdx + 1].id)}>Proceed</Button>
            ) : (
              <Button onClick={handleSave} disabled={!canSave || saving}>
                {saving ? "Saving..." : policy ? "Save Changes" : "Save Policy"}
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Reusable: Section card ─────────────────────────────────────────

function Section({ title, required, children, description }: { title: string; required?: boolean; description?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div>
        <Label className="text-xs font-semibold">
          {title} {required && <span className="text-destructive">*</span>}
        </Label>
        {description && <p className="text-[11px] text-muted-foreground mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  );
}

function ChipMulti<T extends string>({ options, selected, onToggle }: { options: readonly T[]; selected?: T[]; onToggle: (v: T) => void }) {
  const sel = selected || [];
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(o => {
        const isOn = sel.includes(o);
        return (
          <Badge
            key={o}
            variant={isOn ? "default" : "outline"}
            className={cn("cursor-pointer text-xs h-7 px-3 transition-all", isOn && "shadow-sm")}
            onClick={() => onToggle(o)}
          >
            {isOn && <Check className="h-3 w-3 mr-1" />}
            {o}
          </Badge>
        );
      })}
    </div>
  );
}

function sameId(a: unknown, b: unknown) {
  return String(a ?? "") === String(b ?? "");
}

// ─── Tab 1: Preferred Deal Terms ─────────────────────────────────────

function DealTermsTab({ prefs, setPrefs }: { prefs: InvestmentPreference; setPrefs: React.Dispatch<React.SetStateAction<InvestmentPreference>>; }) {
  const toggle = <K extends keyof InvestmentPreference>(key: K, value: any) => {
    setPrefs(prev => {
      const arr = (prev[key] as any[]) || [];
      const next = arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value];
      return { ...prev, [key]: next };
    });
  };

  return (
    <div className="space-y-4">
      <Section title="Preferred Products" required>
        <ChipMulti
          options={LEASING_PRODUCTS}
          selected={prefs.preferredProducts as any}
          onToggle={(v) => toggle("preferredProducts", v)}
        />
      </Section>

      <Section title="Exposure Limits" required description="Minimum and maximum ticket size per deal">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-muted-foreground">Min Exposure</Label>
            <div className="relative mt-1">
              <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input type="number" value={prefs.minExposure || ""} onChange={e => setPrefs(p => ({ ...p, minExposure: +e.target.value }))} className="h-9 pl-7" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-muted-foreground">Max Exposure</Label>
            <div className="relative mt-1">
              <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input type="number" value={prefs.maxExposure || ""} onChange={e => setPrefs(p => ({ ...p, maxExposure: +e.target.value }))} className="h-9 pl-7" />
            </div>
          </div>
        </div>
      </Section>

      <Section title="Discounting Factor" required description="IRR/Yield range the lender will discount at">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-muted-foreground">Min</Label>
            <div className="relative mt-1">
              <Input type="number" step="0.01" value={prefs.discountingFactorMin || ""} onChange={e => setPrefs(p => ({ ...p, discountingFactorMin: +e.target.value }))} className="h-9 pr-8" />
              <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-muted-foreground">Max</Label>
            <div className="relative mt-1">
              <Input type="number" step="0.01" value={prefs.discountingFactorMax || ""} onChange={e => setPrefs(p => ({ ...p, discountingFactorMax: +e.target.value }))} className="h-9 pr-8" />
              <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            </div>
          </div>
        </div>
      </Section>

      <div className="grid grid-cols-2 gap-4">
        <Section title="Rental Frequency" required>
          <ChipMulti
            options={FREQUENCY_OPTIONS as readonly RentalFrequency[]}
            selected={prefs.frequency}
            onToggle={(v) => toggle("frequency", v)}
          />
        </Section>
        <Section title="Case Type" required>
          <ChipMulti
            options={CASE_TYPE_OPTIONS as readonly CaseType[]}
            selected={prefs.caseTypes}
            onToggle={(v) => toggle("caseTypes", v)}
          />
        </Section>
      </div>
    </div>
  );
}

// ─── Tab 2: Asset Terms ──────────────────────────────────────────────

function AssetTermsTab({ prefs, setPrefs }: { prefs: InvestmentPreference; setPrefs: React.Dispatch<React.SetStateAction<InvestmentPreference>>; }) {
  const [catOpen, setCatOpen] = useState(false);
  const [assetCategories, setAssetCategories] = useState<Array<{ id: number; name: string }>>([]);
  const selectedCatIds = (prefs.assetCategoryRules || []).map(r => String(r.category));

  useEffect(() => {
    apiService
      .get<{ data: Array<{ id: number; name: string }> }>("/lender/get-asset-categories-list")
      .then(res => {
        if (Array.isArray(res.data)) setAssetCategories(res.data);
      });
  }, []);

  useEffect(() => {
    if (!assetCategories.length) return;

    setPrefs(prev => {
      const rules = prev.assetCategoryRules || [];
      let changed = false;
      const nextRules = rules.map((rule) => {
        const current = String(rule.category ?? "");
        if (!current) return rule;
        if (assetCategories.some(c => sameId(c.id, current))) return rule;

        const matched = assetCategories.find(
          c => c.name === current || c.name.toLowerCase() === current.toLowerCase()
        );
        if (!matched) return rule;

        changed = true;
        return { ...rule, category: String(matched.id) };
      });

      return changed ? { ...prev, assetCategoryRules: nextRules } : prev;
    });
  }, [assetCategories, setPrefs]);

  const isCategorySelected = (id: number | string) =>
    selectedCatIds.some(selectedId => sameId(selectedId, id));

  const categoryName = (id: string) =>
    assetCategories.find(c => sameId(c.id, id))?.name ?? id;

  const toggleCategory = (id: number) => {
    const idStr = String(id);
    setPrefs(prev => {
      const rules = prev.assetCategoryRules || [];
      const exists = rules.find(r => sameId(r.category, idStr));
      const next = exists
        ? rules.filter(r => !sameId(r.category, idStr))
        : [...rules, { category: idStr, tenureMin: 0, tenureMax: 0, preferredAssets: [] }];
      return { ...prev, assetCategoryRules: next };
    });
  };

  const updateRule = (idx: number, patch: Partial<AssetCategoryRule>) => {
    setPrefs(prev => {
      const rules = [...(prev.assetCategoryRules || [])];
      rules[idx] = { ...rules[idx], ...patch };
      return { ...prev, assetCategoryRules: rules };
    });
  };

  const addAsset = (idx: number, val: string) => {
    const v = val.trim();
    if (!v) return;
    const rule = prefs.assetCategoryRules[idx];
    const list = rule.preferredAssets || [];
    if (list.includes(v)) return;
    updateRule(idx, { preferredAssets: [...list, v] });
  };
  const removeAsset = (idx: number, val: string) => {
    const rule = prefs.assetCategoryRules[idx];
    updateRule(idx, { preferredAssets: (rule.preferredAssets || []).filter(x => x !== val) });
  };

  return (
    <div className="space-y-4">
      <Section title="Asset Categories" required description="Select all asset categories this policy covers">
        <Popover open={catOpen} onOpenChange={setCatOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" className="w-full justify-between h-9 font-normal">
              <span className="truncate text-sm">
                {selectedCatIds.length === 0
                  ? "Select asset categories..."
                  : selectedCatIds.map(categoryName).join(", ")}
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0 z-[100]" align="start">
            <div
              className="max-h-64 overflow-y-auto overscroll-contain p-1"
              onWheelCapture={e => e.stopPropagation()}
            >
              {assetCategories.map(cat => {
                const sel = isCategorySelected(cat.id);
                return (
                  <div
                    key={cat.id}
                    role="option"
                    aria-selected={sel}
                    tabIndex={0}
                    onClick={() => toggleCategory(cat.id)}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleCategory(cat.id);
                      }
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-accent text-left cursor-pointer"
                  >
                    <Checkbox checked={sel} className="pointer-events-none" />
                    <span>{cat.name}</span>
                  </div>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        {selectedCatIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {selectedCatIds.map(c => (
              <Badge key={c} variant="secondary" className="text-xs h-6 gap-1">
                {categoryName(c)}
                <button onClick={() => toggleCategory(Number(c))} className="hover:text-destructive"><X className="h-3 w-3" /></button>
              </Badge>
            ))}
          </div>
        )}
      </Section>

      {(prefs.assetCategoryRules || []).length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center bg-muted/20">
          <Info className="h-6 w-6 mx-auto mb-2 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">Select at least one asset category above to configure tenure and preferred assets.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {prefs.assetCategoryRules.map((rule, idx) => (
            <div key={rule.category} className="rounded-lg border bg-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="default" className="text-xs">{categoryName(rule.category)}</Badge>
                </div>
                <Button size="sm" variant="ghost" className="h-7 text-destructive hover:text-destructive" onClick={() => toggleCategory(Number(rule.category))}>
                  <X className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] text-muted-foreground">Min Tenure (months) <span className="text-destructive">*</span></Label>
                  <Input type="number" value={rule.tenureMin || ""} onChange={e => updateRule(idx, { tenureMin: +e.target.value })} className="h-9 mt-1" />
                </div>
                <div>
                  <Label className="text-[11px] text-muted-foreground">Max Tenure (months) <span className="text-destructive">*</span></Label>
                  <Input type="number" value={rule.tenureMax || ""} onChange={e => updateRule(idx, { tenureMax: +e.target.value })} className="h-9 mt-1" />
                </div>
              </div>

              <div>
                <Label className="text-[11px] text-muted-foreground">Preferred Assets <span className="text-muted-foreground/70">(optional)</span></Label>
                <p className="text-[10px] text-muted-foreground mt-0.5 mb-1.5">
                  e.g. CNC Machines, Diesel Generators, Transformers — type and press Enter
                </p>
                <div className="flex flex-wrap gap-1 mb-2">
                  {(rule.preferredAssets || []).map(a => (
                    <Badge key={a} variant="secondary" className="text-xs h-6 gap-1">
                      {a}
                      <button onClick={() => removeAsset(idx, a)} className="hover:text-destructive"><X className="h-3 w-3" /></button>
                    </Badge>
                  ))}
                </div>
                <Input
                  placeholder={`Add preferred ${categoryName(rule.category).toLowerCase()} asset...`}
                  className="h-9"
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addAsset(idx, (e.target as HTMLInputElement).value);
                      (e.target as HTMLInputElement).value = "";
                    }
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Tab 3: Customer Profile ─────────────────────────────────────────

function CustomerProfileTab({ prefs, setPrefs }: { prefs: InvestmentPreference; setPrefs: React.Dispatch<React.SetStateAction<InvestmentPreference>>; }) {
  const [indOpen, setIndOpen] = useState(false);
  const [industryTypes, setIndustryTypes] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    apiService
      .get<{ data: Array<{ id: number; name: string }> }>("/customers/industry-types")
      .then(res => {
        if (Array.isArray(res.data)) setIndustryTypes(res.data);
      });
  }, []);

  useEffect(() => {
    if (!industryTypes.length) return;

    setPrefs(prev => {
      const list = prev.negativeIndustry || [];
      let changed = false;
      const next = list.map((value) => {
        const current = String(value ?? "");
        if (!current) return current;
        if (industryTypes.some(t => sameId(t.id, current))) return current;

        const matched = industryTypes.find(
          t => t.name === current || t.name.toLowerCase() === current.toLowerCase()
        );
        if (!matched) return current;

        changed = true;
        return String(matched.id);
      });

      return changed ? { ...prev, negativeIndustry: next } : prev;
    });
  }, [industryTypes, setPrefs]);

  const selectedIndustryIds = (prefs.negativeIndustry || []).map(id => String(id));

  const isIndustrySelected = (id: number | string) =>
    selectedIndustryIds.some(selectedId => sameId(selectedId, id));

  const industryName = (id: string) =>
    industryTypes.find(t => sameId(t.id, id))?.name ?? id;

  const toggleIndustry = (id: number) => {
    const idStr = String(id);
    setPrefs(prev => {
      const list = prev.negativeIndustry || [];
      const next = list.some(x => sameId(x, idStr))
        ? list.filter(x => !sameId(x, idStr))
        : [...list, idStr];
      return { ...prev, negativeIndustry: next };
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Section title="Min Credit Rating" required>
          <Select value={prefs.minCreditRating} onValueChange={v => setPrefs(p => ({ ...p, minCreditRating: v }))}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select rating" />
            </SelectTrigger>
            <SelectContent>
              {CREDIT_RATINGS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </Section>

        <Section title="Min Turnover" required description="Minimum customer annual turnover">
          <div className="relative">
            <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="number"
              value={prefs.minTurnover || ""}
              onChange={e => setPrefs(p => ({ ...p, minTurnover: +e.target.value }))}
              className="h-9 pl-7"
              placeholder="0"
            />
          </div>
        </Section>
      </div>

      <Section title="Negative Industries" description="Customers from these industries will not be eligible">
        <Popover open={indOpen} onOpenChange={setIndOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" className="w-full justify-between h-9 font-normal">
              <span className="truncate text-sm">
                {selectedIndustryIds.length === 0
                  ? "Select industries to restrict..."
                  : selectedIndustryIds.map(industryName).join(", ")}
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0 z-[100]" align="start">
            <div
              className="max-h-64 overflow-y-auto overscroll-contain p-1"
              onWheelCapture={e => e.stopPropagation()}
            >
              {industryTypes.map(ind => {
                  const sel = isIndustrySelected(ind.id);
                  return (
                    <div
                      key={ind.id}
                      role="option"
                      aria-selected={sel}
                      tabIndex={0}
                      onClick={() => toggleIndustry(ind.id)}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleIndustry(ind.id);
                        }
                      }}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-accent text-left cursor-pointer"
                    >
                      <Checkbox checked={sel} className="pointer-events-none" />
                      <span>{ind.name}</span>
                    </div>
                  );
                })}
            </div>
          </PopoverContent>
        </Popover>
        {(prefs.negativeIndustry || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {selectedIndustryIds.map(v => (
              <Badge key={v} variant="secondary" className="text-xs h-6 gap-1">
                {industryName(v)}
                <button onClick={() => toggleIndustry(Number(v))} className="hover:text-destructive"><X className="h-3 w-3" /></button>
              </Badge>
            ))}
          </div>
        )}
      </Section>

      <Section title="Note" description="Internal note about this policy (visible to all RMs)">
        <Textarea
          value={prefs.note || ""}
          onChange={e => setPrefs(p => ({ ...p, note: e.target.value }))}
          placeholder="e.g. Prefers customers with established vintage > 3 years. Avoid first-time leasing clients."
          className="min-h-[88px] text-sm"
        />
      </Section>
    </div>
  );
}
